import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@shared/api/client';

import { shelvesKey, tagStripKey } from './queries';
import { layoutFor, sourceParamFields } from './Shelves.vocabulary';
import type { Shelf, ShelfDraft, ShelfListPayload, ShelfSurface, TagStripPayload, WireShelf } from './types';

/**
 * The four writes this surface owns.
 *
 * **The bodies are the fields the service declares, one per column.** The
 * request models carry `sourceParamKind` / `sourceParamValue` / `orderedIds`
 * and forbid an unknown key outright, so the composed `sourceParam` object and
 * the `shelfIds` list this file used to send were refused as `422` before any
 * of them reached a column — every write on this surface failed, and the
 * read path's own composition is what made that invisible from here.
 *
 * **The pair is derived from the source, once.** A parameterless source must
 * carry `null` on both halves — a parameter on one is `422` — so the body is
 * built from the source rather than from whatever the picker last held. That is
 * also why the dialog's own schema refuses the same shape: two guards, and the
 * server's is still the authority.
 *
 * **`layout` is required on create and is not asked for.** The catalog forbids
 * a layout picker, so it is authored from the source — see `layoutFor`. It is
 * absent from the update body, which leaves the shelf's own shape untouched:
 * the service applies only the keys the caller set.
 *
 * **`confirm=true` is the wire half of the dialog.** The delete below is the
 * only place in this feature that sets it, and it is reachable only from a
 * `<ConfirmDialog>` accept handler. A code path setting it from anywhere else
 * has removed the guard while leaving the dialog on screen.
 *
 * **No mutation auto-retries** — the client's `retry` policy covers reads only.
 */

export function useCreateShelf(surface: ShelfSurface) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (draft: ShelfDraft) =>
      api.post<WireShelf>('content', '/admin/shelves', {
        surface,
        title: draft.title,
        layout: layoutFor(draft.source),
        source: draft.source,
        ...sourceParamFields(draft.source, draft.paramValues),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: shelvesKey(surface) }),
  });
}

export function useUpdateShelf(surface: ShelfSurface) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ shelfId, draft }: { shelfId: string; draft: ShelfDraft }) =>
      api.patch<WireShelf>('content', `/admin/shelves/${shelfId}`, {
        title: draft.title,
        source: draft.source,
        ...sourceParamFields(draft.source, draft.paramValues),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: shelvesKey(surface) }),
  });
}

export function useDeleteShelf(surface: ShelfSurface) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (shelfId: string) => api.delete<null>('content', `/admin/shelves/${shelfId}?confirm=true`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: shelvesKey(surface) }),
  });
}

/**
 * Reorder — the whole ordered id list for one surface, in one transaction, the
 * same shape the sibling taxonomy reorder takes.
 *
 * **This one write is optimistic, and the reason is the interaction rather than
 * the risk.** A reorder handle whose row does not move until a round trip
 * completes reads as a broken control, and the announcement and the focus
 * restore both have to describe a position that already exists. The cache is
 * written first, rolled back on failure, and invalidated either way — so the
 * server stays the authority on what the order actually is.
 *
 * It is not the class the repo forbids optimism on: nothing is destroyed,
 * nothing is published, and no configuration is written.
 */
export function useReorderShelves(surface: ShelfSurface) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (shelfIds: string[]) =>
      // `orderedIds` — the whole surface's order, under the name the reorder
      // model declares. `shelfIds` was an unknown key on a model that forbids
      // them, so every reorder was refused and rolled straight back.
      api.post<{ surface: string; reordered: number }>('content', '/admin/shelves:reorder', {
        surface,
        orderedIds: shelfIds,
      }),

    onMutate: async (shelfIds: string[]) => {
      const queryKey = shelvesKey(surface);
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<ShelfListPayload>(queryKey);
      if (!previous) return { previous };

      const byId = new Map(previous.items.map((shelf) => [shelf.id, shelf]));
      const items = shelfIds
        .map((id, index) => {
          const shelf = byId.get(id);
          return shelf ? { ...shelf, position: index + 1 } : null;
        })
        .filter((shelf): shelf is Shelf => shelf !== null);

      queryClient.setQueryData<ShelfListPayload>(queryKey, { ...previous, items });
      return { previous };
    },

    onError: (_error, _shelfIds, context) => {
      if (context?.previous) queryClient.setQueryData(shelvesKey(surface), context.previous);
    },

    onSettled: () => queryClient.invalidateQueries({ queryKey: shelvesKey(surface) }),
  });
}

/**
 * Rewrite the curated chip strip.
 *
 * **One `PUT` carrying the whole ordered list, and no reorder route beside it.**
 * Membership and rank are one decision here — an operator is choosing which
 * terms the strip carries and in what order — so a create/reorder pair would
 * split one choice across two requests and leave a window where the strip holds
 * a set nobody authored.
 *
 * **Not optimistic.** The reorder on the shelf table is, because a handle that
 * does not move reads as broken; this is a form with a save button, where the
 * committed state arriving from the server is exactly what the operator is
 * waiting to see.
 */
export function useReplaceTagStrip() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderedIds: string[]) =>
      api.put<TagStripPayload>('content', '/admin/tagStrip', { orderedIds }),
    onSuccess: (payload) => {
      queryClient.setQueryData(tagStripKey(), payload);
      void queryClient.invalidateQueries({ queryKey: tagStripKey() });
    },
  });
}
